import React, { useState } from 'react';
import { Button, Form, Input, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useDataProvider, useNotify, usePermissions, useRedirect } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { ExtendedDataProvider } from '@src/types';
import { Field, FormSection, NetorcaForm, useServerErrors } from '@src/components/Common/Form';
import { EntityFormRoute, ServiceField } from '@src/components/Common/Integrations';
import MainCard from '@src/components/Common/MainCard/MainCard';
import DeleteButton from '@src/components/Common/DeleteButton';
import MonacoEditorWrapper from '@src/components/Common/MonacoEditorWrapper';
import './DocumentForm.css';

const RESOURCE = 'serviceowner_pack_documents';
const LIST_PATH = `/${RESOURCE}`;

/** DocumentSerializer.Meta.fields, less the read-only ones the form never sends */
const DOCUMENT_FIELDS = ['service', 'filename', 'raw_content', 'enabled'];

/** what the reader can populate the form from — text the pack can index */
const ACCEPTED_FILES = '.txt,.md,.json,.xml,.csv,.log';

/** the editor's frame, in the form theme's border rather than Monaco's own */
const EDITOR_FRAME = { borderColor: '#e1e6f2', borderRadius: 10, backgroundColor: '#ffffff' };

/** short enough to leave the file picker below it on screen */
const EDITOR_HEIGHT = '260px';

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

const DocumentForm = ({ record }: { record?: any }) => {
    const [form] = Form.useForm();
    const { permissions } = usePermissions();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const location = useLocation();
    const { apply, fieldErrors, clear } = useServerErrors(form, DOCUMENT_FIELDS);
    const [saving, setSaving] = useState(false);
    const [fileList, setFileList] = useState<any[]>([]);
    const [fileSize, setFileSize] = useState(0);

    const isAdmin = permissions === 'ADMIN';
    const editing = !!record;

    const charCount = Form.useWatch('raw_content', form)?.length || 0;

    // The list links here from a service it is already filtered to, under
    // either name it has been written with.
    const params = new URLSearchParams(location.search);
    const serviceIdRaw = params.get('service_id') || params.get('service');
    const serviceIdFromUrl = serviceIdRaw && !Number.isNaN(parseInt(serviceIdRaw, 10))
        ? parseInt(serviceIdRaw, 10)
        : undefined;

    const handleFileUpload = (file: File) => {
        setFileSize(file.size);
        const reader = new FileReader();
        reader.onload = (e) => {
            form.setFieldsValue({ filename: file.name, raw_content: e.target?.result as string });
            notify('Filename and content taken from the file');
        };
        reader.onerror = () => notify('Could not read that file', 'error');
        reader.readAsText(file);
        // the form holds the text now; there is nothing to upload separately
        return false;
    };

    const onFinish = (values: any) => {
        // A PUT that changes nothing still counts as a save server-side, so the
        // page says so rather than making the round trip. Both editable fields
        // are checked: a rename on its own is a real change.
        if (editing && values.filename === record.filename && values.raw_content === record.raw_content) {
            notify('Nothing has changed', 'warning');
            return;
        }

        clear();
        setSaving(true);

        // Creating goes through the service's own route, which upserts on
        // filename; `enabled` belongs to the list's switch, so an edit has to
        // carry the value it already has past the PUT.
        const request = editing
            ? dataProvider.update(RESOURCE, {
                  id: record.id,
                  data: { ...values, enabled: record.enabled },
                  previousData: { id: record.id },
              })
            : dataProvider.createAction(RESOURCE, {
                  id: undefined,
                  verb: `service/${values.service}/`,
                  data: { filename: values.filename, raw_content: values.raw_content },
              });

        request
            .then(() => {
                notify(editing ? 'Document updated' : 'Document created');
                redirect(LIST_PATH);
            })
            .catch((error: any) => {
                apply(error, editing ? 'Could not update the document' : 'Could not create the document');
                setSaving(false);
            });
    };

    return (
        <NetorcaForm
            className="nf-form--sheet netorca-doc-form"
            form={form}
            name="document"
            /* no eyebrow: the arrow beside the name already says what list it
               belongs to, and the name is what the page is about */
            title={editing ? record.filename : 'New document'}
            onBack={() => redirect(LIST_PATH)}
            backLabel="Back to documents"
            readOnly={!isAdmin}
            submitLabel={editing ? 'Save changes' : 'Create document'}
            submitting={saving}
            onCancel={() => redirect(LIST_PATH)}
            onFinish={onFinish}
            serverErrors={fieldErrors}
            destructive={
                editing && isAdmin ? <DeleteButton title="Document" resource={RESOURCE} record={record} /> : undefined
            }
            initialValues={
                record
                    ? { ...record, service: record.service?.id ?? record.service }
                    : { service: serviceIdFromUrl }
            }
        >
            {/* One grid for the whole form, read top to bottom: what the
                document is called and what it belongs to, then the text
                itself, then the file it can be taken from instead. */}
            <FormSection id="document" title="Document" bare>
                <Field
                    name="filename"
                    label="Filename"
                    span={6}
                    rules={[{ required: true, message: 'Give the document a filename' }]}
                >
                    <Input placeholder="documentation.txt" />
                </Field>

                <ServiceField
                    span={6}
                    // the create route is the service's own, and the API rejects
                    // a change of service on a document that already exists —
                    // the disabled control says so without a line of its own
                    locked={editing || !!serviceIdFromUrl}
                />

                <Field
                    name="raw_content"
                    label="Content"
                    className="doc-content"
                    hint={charCount > 0 ? `${charCount.toLocaleString()} characters` : undefined}
                    rules={[{ required: true, message: 'The document needs some content' }]}
                >
                    <MonacoEditorWrapper readOnly={!isAdmin} height={EDITOR_HEIGHT} style={EDITOR_FRAME} />
                </Field>

                {isAdmin && (
                    <Field
                        label="Load from a file"
                        hint={
                            fileSize > 0
                                ? `Read ${formatFileSize(fileSize)} into the fields above.`
                                : "Replaces the filename and the content above with the file's."
                        }
                    >
                        <Upload
                            fileList={fileList}
                            beforeUpload={handleFileUpload}
                            onChange={({ fileList: next }) => setFileList(next)}
                            onRemove={() => {
                                setFileList([]);
                                setFileSize(0);
                            }}
                            maxCount={1}
                            accept={ACCEPTED_FILES}
                        >
                            <Button icon={<UploadOutlined />}>Choose a text file</Button>
                        </Upload>
                    </Field>
                )}
            </FormSection>
        </NetorcaForm>
    );
};

const DocumentCreate = (props: any) => (
    <EntityFormRoute
        id={props.id}
        resource={RESOURCE}
        basePath={LIST_PATH}
        // the list payload carries every field on this form but the content —
        // the API strips it there — so the cached record is not one to seed from
        isComplete={(record) => record.raw_content !== undefined}
        render={(record) => (
            <MainCard>
                <DocumentForm record={record} />
            </MainCard>
        )}
    />
);

export default DocumentCreate;
